import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Divider, Typography } from '@mui/material';
import {
  IconDefinition,
  faUsers,
  faBadgePercent,
} from '@fortawesome/pro-light-svg-icons';
import { CommunityStateToggle } from '@client/new/community/components/CommunityStateToggle';
import { ReactNode } from 'react';
import { FreeTrialToggle } from '@client/new/pages/settings/FreeTrialToggle';
import auth from './auth';
import {
  faEnvelopeDot,
  faSignalStream,
} from '@fortawesome/pro-regular-svg-icons';
import { LivestreamingToggle } from '@client/new/pages/settings/LivestreamingToggle';
import { DirectMessagesToggle } from '@client/new/pages/settings/DirectMessagesToggle';
import { Influencer } from '@client/new/pages/settings/useSettings.viewModel';
import { isSubscriptionCreator } from '@client/new/common/offering.helpers';

type FeaturesConfig = {
  title: string;
  icon: IconDefinition;
  component: ReactNode;
  description?: string;
  hideForModerators?: boolean;
};

const getFeatureSettingsConfig = (influencer: Influencer): FeaturesConfig[] => [
  {
    icon: faUsers,
    title: 'Community',
    component: <CommunityStateToggle influencer={influencer} />,
  },
  ...(isSubscriptionCreator(influencer?.offering)
    ? [
        {
          icon: faBadgePercent,
          title: '7-Day Free Trial',
          component: (
            <FreeTrialToggle
              influencerId={influencer.id}
              enableCheckoutFreeTrial={!!influencer.enableCheckoutFreeTrial}
            />
          ),
          hideForModerators: true,
        },
      ]
    : []),
  {
    icon: faSignalStream,
    title: 'Livestreaming',
    component: (
      <LivestreamingToggle
        influencerId={influencer.id}
        isEnabledLivestreaming={!!influencer.customizations.canLiveStream}
      />
    ),
  },
  {
    icon: faEnvelopeDot,
    title: 'Direct Messages',
    component: (
      <DirectMessagesToggle
        influencerId={influencer.id}
        isEnabledMessaging={!!influencer.customizations.canDirectMessage}
      />
    ),
  },
];

export const FeatureSettings = ({ influencer }: { influencer: Influencer }) => {
  const features = getFeatureSettingsConfig(influencer);
  const isModerator = auth.isModerator();

  return (
    <div className="flex flex-col gap-4">
      {features.map(
        ({
          title,
          description,
          icon,
          component,
          hideForModerators = false,
        }) => (
          <div key={title}>
            {hideForModerators && isModerator ? null : (
              <Box className="flex justify-between">
                <Box className="flex gap-[15px]">
                  <Box className="w-auto h-auto flex justify-center items-center">
                    <FontAwesomeIcon
                      icon={icon}
                      className="text-primary text-[24px]"
                    />
                  </Box>
                  <Box className="flex flex-col justify-center gap-[5px]">
                    <Typography className="font-[700] m-0">{title}</Typography>
                    {description && (
                      <Typography className="text-gray-4 text-[14px] m-0">
                        {description}
                      </Typography>
                    )}
                  </Box>
                </Box>
                <Box>{component}</Box>
              </Box>
            )}
          </div>
        ),
      )}
    </div>
  );
};
